/**
 * What Search (⌘K) lists for a query: the matching tickets on every Team the
 * reader can see, by the Team queue's own rules (`rowMatchesSearch`, the five
 * groups, the archive). One group per Team, the Team on screen first, then the
 * rest in the rail's order; inside each, what is owed a person first and the
 * archive last, newest first within a status. A Team keeps `perTeam` hits and
 * counts the rest, which its own page lists in full.
 */
import type { WorkItem, WorkSearchQuery } from "@yagni/shared";
import { archivedRows, queueGroupOf, rowMatchesSearch } from "../ticket/queueGroups.js";
import { STATUS_FILTERS, type StatusFilter } from "../ticket/statusFilter.js";
import { activityAtOf, driverOf, foldPipelineRows, type PipelineRow } from "../work/workViews.js";

/** How many tickets a Team shows before "N more on <Team>". */
export const SEARCH_PER_TEAM = 3;

export interface SearchHit {
  row: PipelineRow;
  status: StatusFilter;
}

export interface SearchTeamGroup {
  deskId: string;
  hits: SearchHit[];
  /** Every match on the Team, the hits included. */
  total: number;
}

export interface SearchEveryTeamOptions {
  /** The Teams the reader can see, in the rail's order. Matches on any other Team are dropped. */
  deskIds: readonly string[];
  /** The Team on screen, listed first. */
  firstDeskId: string | null;
  now: number;
  perTeam?: number;
}

function timeOf(iso: string | null | undefined): number {
  const at = iso ? Date.parse(iso) : Number.NaN;
  return Number.isFinite(at) ? at : 0;
}

export function searchEveryTeam(
  items: readonly WorkItem[],
  query: WorkSearchQuery,
  { deskIds, firstDeskId, now, perTeam = SEARCH_PER_TEAM }: SearchEveryTeamOptions,
): SearchTeamGroup[] {
  const hits: SearchHit[] = [];
  for (const row of foldPipelineRows(items.filter((item) => !item.archivedAt))) {
    const status = queueGroupOf(row, now);
    if (status && rowMatchesSearch(row, query)) hits.push({ row, status });
  }
  for (const row of archivedRows(items)) {
    if (rowMatchesSearch(row, query)) hits.push({ row, status: "archived" });
  }

  const byDesk = new Map<string, SearchHit[]>();
  for (const hit of hits) {
    const deskId = hit.row.anchor.deskId;
    if (!deskId) continue;
    const bucket = byDesk.get(deskId);
    if (bucket) bucket.push(hit);
    else byDesk.set(deskId, [hit]);
  }

  const rank = (hit: SearchHit) => STATUS_FILTERS.indexOf(hit.status);
  const when = (hit: SearchHit) =>
    hit.status === "archived"
      ? timeOf(hit.row.anchor.archivedAt)
      : timeOf(activityAtOf(driverOf(hit.row)));
  const order =
    firstDeskId && deskIds.includes(firstDeskId)
      ? [firstDeskId, ...deskIds.filter((id) => id !== firstDeskId)]
      : deskIds;
  return order.flatMap((deskId) => {
    const bucket = byDesk.get(deskId);
    if (!bucket) return [];
    const sorted = [...bucket].sort((a, b) => rank(a) - rank(b) || when(b) - when(a));
    return [{ deskId, hits: sorted.slice(0, perTeam), total: sorted.length }];
  });
}
